<template>
  <div :style="{ height: content.height }" class="w-full">
    <LMap
      ref="map"
      :zoom="zoom"
      :min-zoom="ZOOM_LEVEL_MIN"
      :max-zoom="ZOOM_LEVEL_MAX"
      @update:zoom="(newZoom: number) => zoom = newZoom"
      :use-global-leaflet="true"
      @ready="onMapReady"
      @update:bounds="onMapUpdated"
      :options="{
        attributionControl: false,
        zoomControl: false,
        gestureHandling: true,
        gestureHandlingText: {
          touch: $t('dbc-map-gesture-handling-touch'),
          scroll: $t('dbc-map-gesture-handling-scroll'),
          scrollMac: $t('dbc-map-gesture-handling-scroll-mac'),
        },
      }"
    >
      <LControlAttribution prefix="" position="bottomright" />
      <LControl position="topleft">
        <AutoComplete
          :suggestions="autoCompleteOptions"
          optionLabel="address"
          @complete="search"
          @option-select="({value}: AutoCompleteOptionSelectEvent) => setLocation({latitude: value.latitude, longitude: value.longitude})"
          :showEmptyMessage="false"
          :placeholder="$t('dbc-text-find-bikes-near-you')"
          pt:root="relative inline-flex shadow-[2px_2px_5px_1px_rgba(0,0,0,0.3)]"
          pt:pcInputText:root:class="text-[1rem] w-sm h-[2.8rem] border-none pl-10 rounded-sm bg-search"
          pt:list:class="bg-white list-none m-0 p-0 mt-1 rounded-sm w-sm"
          pt:option:class="px-1 py-2 cursor-pointer hover:bg-cool-gray-200 px-2"
          pt:loader:class="absolute right-4 top-0 bottom-0 h-full animate-spin"
        />
      </LControl>
      <LControlZoom position="topright" />
      <LTileLayer
        :url="`https://osm-prod.noncd.db.de/services/api/maps/dbs-osm-basic/{z}/{x}/{y}.jpg?key=${config.public.dbOsmApiKey}`"
        :attribution="`&amp;copy; <a href='https://www.maptiler.com/'>MapTiler</a> | &amp;copy; <a href='https://www.openstreetmap.org/'>OpenStreetMap</a> contributors`"
        layer-type="base"
        name="OpenStreetMap"
      />
      <LMarker
        v-if="zoom < ZOOM_LEVEL_TO_SHOW_OPERATION_AREAS"
        v-for="city in cities"
        :lat-lng="[city.lat, city.lng]"
        :zIndexOffset="city.numberOfBikes * 10"
        @click="onClickCity(city)"
      >
        <LIcon v-if="city.provider === 'ICE'">
          <image
            class="shadow-md rounded-full"
            src="/map/shared/ice.svg"
            :alt="city.name"
          />
        </LIcon>
        <LIcon v-else>
          <div
            class="w-fit rounded-full flex flex-row gap-1 px-2 shadow-md"
            :class="`${getBgClass(city.provider)}`"
          >
            <image class="w-full" src="/map/shared/city.svg" :alt="city.name" />
            <span
              class="text-white font-neo-screen-sans w-full py-1 rounded-full whitespace-nowrap"
              :class="`${getBgClass(city.provider)}`"
            >
              {{ city.name }}
            </span>
          </div>
        </LIcon>
      </LMarker>
      <LPolygon
        v-if="
          operationAreaPolygons.length > 0 &&
          zoom >= ZOOM_LEVEL_TO_SHOW_OPERATION_AREAS
        "
        :lat-lngs="overlayPolygon"
        :opacity="BASE_POLYGON_OPACITY"
        :color="BASE_BORDER_COLOR"
        :weight="BASE_BORDER_WIDTH"
        :fillColor="BASE_FILL_COLOR"
        :fill="true"
        :fillOpacity="BASE_OPACITY"
      />
      <LPolygon
        v-if="
          noParkingAreasPolygons.length > 0 &&
          zoom >= ZOOM_LEVEL_TO_SHOW_NO_PARKING_AREAS
        "
        :lat-lngs="noParkingAreasPolygons"
        :opacity="NO_PARKING_AREA_BORDER_OPACITY"
        :color="NO_PARKING_AREA_BORDER_COLOR"
        :weight="NO_PARKING_AREA_BORDER_WIDTH"
        :fillColor="NO_PARKING_AREA_FILL_COLOR"
        :fill="true"
        :fillOpacity="NO_PARKING_AREA_OPACITY"
      />
    </LMap>

    <DetailsView v-model="isDetailsViewVisible" :details="currentPoint" />
  </div>
</template>

<script setup lang="ts">
import type { LatLngBounds, Map as MapType, Marker } from 'leaflet'
import L from 'leaflet'
import type { AutoCompleteOptionSelectEvent } from 'primevue/autocomplete'
import { GestureHandling } from 'leaflet-gesture-handling'
import 'leaflet-gesture-handling/dist/leaflet-gesture-handling.css'

globalThis.L = L

const props = defineProps<{
  content: ContentMap
}>()

const interactedWithMap = useState('interacted-with-map', () =>
  shallowRef<{
    interacted: boolean
    category: 'keypress' | 'movestart' | ''
  }>({
    interacted: false,
    category: '',
  })
)
watch(interactedWithMap, (newValue, oldValue) => {
  if (oldValue.interacted === false) {
    if (newValue.interacted === true) {
      trackEvent('Interaktion mit Beauskunftung', newValue.category)
    }
  }
})

onMounted(() => {
  document.addEventListener(
    'availability',
    (e: CustomEventInit<Availability>) => {
      if (e.detail) {
        onClickAvailability(e.detail)
      }
    }
  )
})

const config = useRuntimeConfig()

const map = ref<{ leafletObject: MapType } | null>(null)

const lat = ref<number>(Number(props.content.latitude))
const long = ref<number>(Number(props.content.longitude))

const zoom = ref(Number(props.content.zoom))

const { markerCluster, addMarkers } = await useDbcLMarkerCluster()

const availabilities = ref<Availability[]>([])
watch(availabilities, (newAvailabilities) => {
  addMarkers(markerCluster, newAvailabilities)
})

const basePolygon = ref(Base_Polygon)
const operationAreaPolygons = ref<L.LatLngExpression[]>([])
const overlayPolygon = computed(
  () => [basePolygon.value, ...operationAreaPolygons.value] as any
)

const autoCompleteOptions = ref<AutocompleteData[]>()
const search = async (event: any) => {
  autoCompleteOptions.value = await fetchAutocompleteData(event.query)
}

const noParkingAreasPolygons = ref<L.LatLngExpression[]>([])

const cities = ref<AvailableCity[] | null>(null)

const onMapReady = () => {
  map.value?.leafletObject.setView([lat.value, long.value])
  map.value?.leafletObject.addLayer(markerCluster)
  L.Map.addInitHook('addHandler', 'gestureHandling', GestureHandling)

  map.value?.leafletObject.on('movestart', () => {
    interactedWithMap.value = {
      interacted: true,
      category: 'movestart',
    }
  })

  map.value?.leafletObject.on('keypress', () => {
    interactedWithMap.value = {
      interacted: true,
      category: 'keypress',
    }
  })
}

const onMapUpdated = async () => {
  const bounds: LatLngBounds | undefined = map.value?.leafletObject.getBounds()

  if (zoom.value < ZOOM_LEVEL_TO_SHOW_OPERATION_AREAS) {
    availabilities.value = []

    if (!cities.value) {
      fetchAvailableCities().then((response: AvailableCitiesResponse) => {
        cities.value = response.data.map((city: AvailableCity) => {
          city.lng = city.location.coordinates[0]
          city.lat = city.location.coordinates[1]
          return city
        })
      })
    }
  } else if (bounds) {
    fetchAvailabilities(bounds).then(
      async (response: AvailabilityResponse | null) => {
        availabilities.value = response?.data
          ? response.data.map((point: Availability) => {
              point.lng = point.location.coordinates[0]
              point.lat = point.location.coordinates[1]
              return point
            })
          : []
      }
    )

    const availableAreasData = await fetchAvailableAreas(bounds)

    operationAreaPolygons.value = availableAreasData.operationAreaPolygons
    noParkingAreasPolygons.value = availableAreasData.noParkingAreasPolygons
  }
}

const onClickCity = (city: AvailableCity) => {
  map.value?.leafletObject.flyTo([city.lat, city.lng], 11, {
    duration: 1,
  })
}

const currentPoint = ref<Availability | null>(null)
const isDetailsViewVisible = ref<boolean>(false)

const onClickAvailability = (point: Availability) => {
  currentPoint.value = point
  isDetailsViewVisible.value = true
}

const getBgClass = (provider: BikeProvider) => {
  switch (provider) {
    case 'SRHH':
      return 'bg-srhh'
    case 'RRS':
      return 'bg-rrs'
    case 'SRLG':
      return 'bg-srlg'
    default:
      return 'bg-cab'
  }
}

const setLocation = (location: { latitude: number; longitude: number }) => {
  map.value?.leafletObject.setView(
    L.latLng(location.latitude, location.longitude),
    ZOOM_LEVEL_MAX
  )
}
</script>
